import React, { useMemo } from "react";
import {
  EmotionalSlideContentFragment,
  EmotionalSlidePromoContentFragment,
  PageBuilderEmotionalContentSliderFragment
} from "types/graphql-types";
import PageBuilderSectionTitle from "./PageBuilderSectionTitle";
import { graphql } from "gatsby";
import { isPresent } from "src/utils/checks";
import {
  BackgroundColor,
  Slider,
  SliderSlide
} from "@silversea-ssc/ui-components-react";
import { cardColorForBackground } from "components/PageBuilder/pageBuilderRenderHelper";
import EmotionalLauncher from "components/EmotionalLauncher/EmotionalLauncher";
import { match } from "ts-pattern";
import { CTAButtons } from "./PageBuilderEmotionalLauncher";
import { useFYC } from "components/FindYourCruise/useFYC";
import EmotionalLauncherCruiseCount from "components/EmotionalLauncher/EmotionalLauncherCruiseCount/EmotionalLauncherCruiseCount";
import { useRemotePromo } from "src/ssc-api/useRemotePromos";
import EmotionalLauncherCountdown from "components/EmotionalLauncher/EmotionalLauncherCountdown/EmotionalLauncherCountdown";
import { dateFromOptionalDate } from "src/utils/date";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import { Geolocation } from "src/utils/localization/geolocation/geolocation";
import { filterActiveSlidesForGeolocation } from "components/HeroBannerSlideshow/utils";

type PageBuilderEmotionalContentSliderProps =
  PageBuilderEmotionalContentSliderFragment & {
    backgroundColor: BackgroundColor;
  };

const PageBuilderEmotionalContentSlider: React.FC<
  React.PropsWithChildren<PageBuilderEmotionalContentSliderProps>
> = ({ heading, backgroundColor, items = [] }) => {
  const cardBackgroundColor = cardColorForBackground(backgroundColor);
  const geolocation = useGeolocation();
  const filteredItems = useMemo(
    () =>
      items
        .filter(isPresent)
        .filter(filterActiveSlidesForGeolocation(geolocation)),
    [geolocation, items]
  );

  return (
    <React.Fragment>
      <PageBuilderSectionTitle {...heading} />
      <Slider
        variantColor={cardBackgroundColor}
        variant={"custom"}
        swiperConfig={{
          spaceBetween: 24,
          slidesPerView: 1,
          centeredSlides: true
        }}
        withLeftOverflow={true}
        loop={false}
      >
        {filteredItems.map((item) => {
          return (
            <SliderSlide key={item?._key}>
              <EmotionalContentSlide {...item} geolocation={geolocation} />
            </SliderSlide>
          );
        })}
      </Slider>
    </React.Fragment>
  );
};

export default PageBuilderEmotionalContentSlider;

const EmotionalContentSlide: React.FC<
  (EmotionalSlidePromoContentFragment | EmotionalSlideContentFragment) & {
    geolocation: Geolocation;
  }
> = (props) => {
  return match(props)
    .with({ __typename: "SanityEmotionalSlideContent" }, (item) => (
      <EmotionalLauncher
        backgroundImage={item.image}
        title={item.title?.localized ?? ""}
        content={item.content?.localized}
        variant={"rounded"}
      >
        <CTAButtons ctaButtons={item.ctaButtons} size="small" />
      </EmotionalLauncher>
    ))
    .with({ __typename: "SanityEmotionalSlidePromoContent" }, (item) => (
      <EmotionalSlidePromoContent {...item} />
    ))
    .otherwise(() => <div>Unknown</div>);
};

const EmotionalSlidePromoContent: React.FC<
  EmotionalSlidePromoContentFragment & { geolocation: Geolocation }
> = (props) => {
  const geolocation = props.geolocation;
  const promoData = useRemotePromo(props.promo?.configuration?.code);
  const fycQuery = new FYCQueryBuilder()
    .filterByPromoCodes(
      [props.promo?.configuration?.algoliaCode].filter(isPresent),
      geolocation.countryCode
    )
    .generate();

  const { filterDefaultSearch, localizedSearchIndexName } = useFYC(fycQuery);
  if (!localizedSearchIndexName) return null;

  if (props.showCruiseCount && !props.showCountdown)
    return (
      <EmotionalLauncherCruiseCount
        backgroundImage={props.image}
        title={props.blockTitle?.localized ?? ""}
        content={props.content?.localized}
        searchQuery={filterDefaultSearch}
        searchIndexName={localizedSearchIndexName}
        variant={"rounded"}
        tokens={promoData.data?.tokenValues}
      >
        <CTAButtons ctaButtons={props.ctaButtons} size="small" />
      </EmotionalLauncherCruiseCount>
    );

  const maybeCountdownDate = promoData.data
    ? dateFromOptionalDate(promoData.data.toDate)
    : undefined;

  if (
    props.showCountdown &&
    maybeCountdownDate &&
    maybeCountdownDate > new Date()
  ) {
    return (
      <EmotionalLauncherCountdown
        backgroundImage={props.image}
        title={props.blockTitle?.localized ?? ""}
        content={props.content?.localized}
        variant={"rounded"}
        tokens={promoData.data?.tokenValues}
        searchQuery={props.showCruiseCount ? filterDefaultSearch : undefined}
        searchIndexName={
          props.showCruiseCount ? localizedSearchIndexName : undefined
        }
        expireDate={maybeCountdownDate}
        countdownLabel={props.countdownLabel?.localized}
      >
        <CTAButtons ctaButtons={props.ctaButtons} size="small" />
      </EmotionalLauncherCountdown>
    );
  }

  return (
    <EmotionalLauncher
      backgroundImage={props.image}
      title={props.blockTitle?.localized ?? ""}
      content={props.content?.localized}
      variant={"rounded"}
      tokens={promoData.data?.tokenValues}
    >
      <CTAButtons ctaButtons={props.ctaButtons} size="small" />
    </EmotionalLauncher>
  );
};

// eslint-disable-next-line import/no-unused-modules
export const fragments = graphql`
  fragment PageBuilderEmotionalContentSlider on SanityEmotionalContentSlider {
    _key
    sectionLayoutConfig {
      ...SectionLayoutConfig
    }
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }
    items {
      ... on SanityEmotionalSlideContent {
        ...EmotionalSlideContent
      }

      ... on SanityEmotionalSlidePromoContent {
        ...EmotionalSlidePromoContent
      }
    }
  }

  fragment EmotionalSlideContent on SanityEmotionalSlideContent {
    __typename
    _key
    locationFilters {
      ...AirNonAirOrLocationFilter
    }
    title {
      localized
    }

    content {
      localized
    }

    ctaButtons {
      ...ButtonCTA
    }

    image {
      ...HeroResponsiveImageWithAlt
    }
    customKey
  }

  fragment EmotionalSlidePromoContent on SanityEmotionalSlidePromoContent {
    __typename
    _key
    locationFilters {
      ...AirNonAirOrLocationFilter
    }
    blockTitle {
      localized
    }

    content {
      localized
    }

    ctaButtons {
      ...ButtonCTA
    }

    image {
      ...HeroResponsiveImageWithAlt
    }

    promo {
      _id
      configuration {
        code
        algoliaCode
      }

      countdownLabel {
        localized
      }
    }

    showCruiseCount

    showCountdown

    countdownLabel {
      localized
    }

    customKey
  }
`;
